import QuestionText from "@/components/QuestionText";
import KreolHint from "@/components/KreolHint";
import KikoCheer from "@/components/KikoCheer";
import KikoPath from "@/components/KikoPath";
import StarCatch from "@/components/StarCatch";
import StartGate from "@/components/StartGate";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Loader2, Palmtree, Rocket, Volume2, X } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { playVoice, stopVoice, readAlongSequence } from "@/lib/tts";
import { isYoung, optionLabel } from "@/lib/subjects";
import { playCorrectDing, playLevelCheer, playEncourage } from "@/lib/sfx";
import OptionPic from "@/components/OptionPic";
import { toast } from "sonner";

const voiceOn = () => localStorage.getItem("aa_voice_on") !== "off";
const FR_NAMES = { Maths: "Maths", English: "Anglais", French: "Français", Science: "Sciences", "History & Geography": "Histoire-Géo", SSEE: "SSEE", "Health & Physical Education": "Santé & EPS" };

const kikoWin = (msg, streak, fr) => (streak >= 10 ? (fr ? "10 de suite ! Champion !" : "10 in a row! Champion!")
  : streak === 5 ? (fr ? "5 de suite ! Wow !" : "5 in a row! Wow!") : streak === 3 ? (fr ? "3 de suite !" : "3 in a row!") : msg);
const kikoTry = (fr) => (fr ? "Essaie encore, tu peux le faire !" : "Keep trying, you can do it!");
export default function HolidayPlayer() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [round, setRound] = useState(null);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const start = () => {
    setRound(null); setResult(null); setError("");
    api.post(`/children/${childId}/holiday/start`, { lang: localStorage.getItem("aa_voice_lang") || "en" })
      .then(({ data }) => setRound(data)).catch((e) => setError(formatError(e.response?.data?.detail) || e.message));
  };
  useEffect(() => { start(); return () => stopVoice(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const back = () => navigate(`/kid/${childId}`);
  return (
    <div className="min-h-screen bg-[#FFF4E5] font-kids text-slate-900 py-6" data-testid="holiday-player">
      <div className="max-w-2xl mx-auto px-5">
        <button onClick={back} className="inline-flex items-center gap-1 text-sm font-semibold" data-testid="holiday-back"><ArrowLeft size={16} /> Back</button>
        <h1 className="text-3xl sm:text-4xl font-black mt-3 flex items-center gap-2"><Palmtree className="text-orange-500" /> {round?.lang === "fr" ? "Révisions de vacances" : "Holiday Pack"}</h1>
        {round && !result && <PackBar done={round.pos} total={round.total} finished={round.finished} fr={round.lang === "fr"} />}
        {error ? <p className="nb-card bg-white p-6 mt-6 text-center text-red-600" data-testid="holiday-error">{error === "holiday_required" ? "The Holiday Pack is open from 15 November to 15 January." : error}</p>
          : result ? <Result r={result} lang={round?.lang} onAgain={start} onBack={back} />
          : !round ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-orange-500" /></div>
          : <Run key={round.session_id} round={round} childId={childId} onDone={setResult} />}
      </div>
    </div>
  );
}

function PackBar({ done, total, finished, fr }) {
  const pct = total ? Math.round((100 * Math.min(done, total)) / total) : 0;
  return (
    <div className="mt-3" data-testid="holiday-pack-bar">
      <div className="h-4 rounded-full bg-white border-2 border-slate-900 overflow-hidden"><div className="h-full bg-orange-400" style={{ width: `${pct}%` }} /></div>
      <p className="font-parent text-sm text-slate-600 mt-1" data-testid="holiday-pack-count">
        {finished ? (fr ? "Pack terminé ! Entraînement bonus" : "Pack finished! Extra practice")
          : fr ? `${Math.min(done, total)} / ${total} questions · termine-les toutes avant le 15 janvier pour gagner 3 avatars de vacances !`
          : `${Math.min(done, total)} / ${total} questions · finish them all before 15 January to win 3 holiday avatars!`}
      </p>
    </div>
  );
}

function Run({ round, childId, onDone }) {
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState(null);
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const qs = round.questions;
  const young = isYoung(round.grade);
  const q = qs[i];
  const lang = q.lang;
  const fr = lang === "fr";
  const opts = q.options.map((o) => optionLabel(o, q.has_image, lang));
  const [prog, setProg] = useState(null);
  const listen = () => { setProg(0); readAlongSequence(q.question, opts, lang, { onQuestionProgress: setProg, onOption: () => {}, onDone: () => setProg(null) }); };
  const [started, setStarted] = useState(!young);
  const [kiko, setKiko] = useState(null);
  const [star, setStar] = useState(0);
  const streakRef = useRef(0);
  useEffect(() => { setProg(null); if (voiceOn() && started) listen(); }, [i]); // eslint-disable-line react-hooks/exhaustive-deps
  const choose = async (k) => {
    if (picked !== null) return;
    setPicked(k);
    stopVoice();
    try {
      const { data } = await api.post(`/children/${childId}/holiday/answer`, { session_id: round.session_id, i, chosen: k });
      setCheck(data);
      streakRef.current = data.correct ? streakRef.current + 1 : 0;
      if (data.correct) {
        playCorrectDing();
        const said = young ? kikoWin(fr ? "Bravo !" : "Well done!", streakRef.current, fr) : (fr ? "Bravo !" : "Well done!");
        if (young) setKiko({ n: Date.now(), msg: said, streak: streakRef.current, kind: "win" });
        else if (streakRef.current % 10 === 0) setKiko({ n: Date.now(), msg: kikoWin(said, 10, fr), streak: 10, kind: "win" });
        if (voiceOn()) setTimeout(() => playVoice(said, lang), 450);
      } else {
        setKiko({ n: Date.now(), msg: kikoTry(fr), kind: "try" });
        if (voiceOn()) setTimeout(() => playVoice(kikoTry(fr), lang), 450);
      }
      if (young ? (i + 1) % 5 === 0 && i + 1 < qs.length : i + 1 === qs.length) setTimeout(() => setStar(Date.now()), young ? 3000 : 1200);
    } catch { setPicked(null); }
  };
  const next = async () => {
    stopVoice();
    if (i < qs.length - 1) { setI(i + 1); setPicked(null); setCheck(null); return; }
    setBusy(true);
    try { const { data } = await api.post(`/children/${childId}/holiday/submit`, { session_id: round.session_id }); onDone(data); }
    catch (e) { toast.error(formatError(e.response?.data?.detail) || (fr ? "Oups, réessaie !" : "Oops, please try again!")); }
    finally { setBusy(false); }
  };
  const img = `${process.env.REACT_APP_BACKEND_URL}/api/question-image?chapter_id=${encodeURIComponent(q.chapter_id)}&level=${q.level}&qi=${q.qi}&lang=${q.subject === "Maths" && fr ? "fr" : "en"}`;
  return (
    <>
    <KikoCheer cheer={kiko} />
    <StarCatch show={star} childId={childId} lang={lang} />
    <motion.div key={i} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="nb-card bg-white p-6 mt-5" data-testid="holiday-question-card">
      <div className="flex items-center justify-between gap-2 flex-wrap text-sm font-parent text-slate-500">
        <span data-testid="holiday-progress">{i + 1} / {qs.length}</span>
        <span className="rounded-full bg-orange-100 text-orange-700 px-3 py-1 font-semibold" data-testid="holiday-subject">{fr ? FR_NAMES[q.subject] || q.subject : q.subject}</span>
        {q.psac && <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-700 px-3 py-1 font-semibold" data-testid="holiday-psac-badge"><Rocket size={14} /> {fr ? "Prépare-toi pour le PSAC !" : "Get ready for the PSAC!"}</span>}
        {q.next_grade && <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-700 px-3 py-1 font-semibold" data-testid="holiday-next-grade-badge"><Rocket size={14} /> {fr ? "Prépare l'année prochaine !" : "Get ready for next year!"}</span>}
      </div>
      {qs.length > 0 && <KikoPath current={i + (picked !== null ? 1 : 0)} total={qs.length} />}
      <div className="flex items-start gap-3 mt-3">
        <p className={`${young ? "text-2xl sm:text-4xl" : "text-xl sm:text-2xl"} font-bold flex-1`} data-testid="holiday-question"><QuestionText text={q.question} activeWord={prog} lang={lang} /></p>
        <button onClick={listen} className="w-12 h-12 rounded-full bg-teal-400 border-2 border-slate-900 flex items-center justify-center shrink-0" data-testid="holiday-listen"><Volume2 size={22} /></button>
      </div>
      {young && !started && <StartGate lang={lang} onStart={() => { setStarted(true); if (voiceOn()) listen(); }} />}
      {picked === null && <KreolHint hint={q.kreol_hint} big={young} onUse={() => api.post(`/children/${childId}/hint-used`, { subject: q.subject, chapter_id: q.chapter_id, level: q.level, qi: q.qi, question: q.question }).catch(() => {})} />}
      {q.has_image && <img src={img} alt="" className="mt-4 mx-auto max-h-72 rounded-2xl border-2 border-slate-200" data-testid="holiday-image" />}
      <div className="grid gap-3 mt-5">
        {opts.map((o, k) => {
          const good = check && k === check.answer;
          const bad = check && k === picked && !check.correct;
          return (
            <button key={k} onClick={() => choose(k)} disabled={picked !== null} data-testid={`holiday-option-${k}`}
              className={`nb-card p-4 text-left ${young ? "text-2xl" : "text-lg"} font-semibold flex items-center gap-3 ${good ? "bg-green-200" : bad ? "bg-red-200" : "bg-white hover:bg-orange-50"}`}>
              <span className="w-8 h-8 rounded-full border-2 border-slate-900 flex items-center justify-center text-sm shrink-0 bg-white">{good ? <Check size={16} /> : bad ? <X size={16} /> : "ABCD"[k]}</span>
              <OptionPic pics={q.option_pics} k={k} big={young} />
              {o}
            </button>
          );
        })}
      </div>
      {check && (
        <div className={`mt-5 rounded-2xl p-4 border-2 ${check.correct ? "bg-green-50 border-green-500" : "bg-amber-50 border-amber-500"}`} data-testid="holiday-feedback">
          <p className="font-bold text-lg">{check.correct ? (fr ? "Bravo !" : "Well done!") : (fr ? "Pas tout à fait…" : "Not quite…")}</p>
          {check.explanation && <p className="font-parent mt-1" data-testid="holiday-explanation">{check.explanation}</p>}
          <button onClick={next} disabled={busy} className="mt-3 rounded-full bg-orange-500 text-white font-bold px-6 py-2.5 border-2 border-slate-900" data-testid="holiday-next">
            {busy ? <Loader2 size={16} className="animate-spin" /> : i < qs.length - 1 ? (fr ? "Suivant" : "Next") : (fr ? "Voir mon score" : "See my score")}
          </button>
        </div>
      )}
    </motion.div>
    </>
  );
}

function Result({ r, lang, onAgain, onBack }) {
  const fr = lang === "fr";
  useEffect(() => {
    if (r.passed || r.new_avatars?.length) playLevelCheer(); else playEncourage();
    if (voiceOn()) playVoice(r.passed ? (fr ? "Bravo ! Super révision !" : "Hooray! Great revision!") : (fr ? "Allez, tu peux y arriver !" : "Come on, you can make it!"), lang);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const left = r.pack_total != null ? Math.max(0, r.pack_total - r.pos) : null;
  return (
    <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="nb-card bg-white p-8 mt-6 text-center" data-testid="holiday-result">
      <p className="text-3xl font-black" style={{ color: r.passed ? "#10B981" : "#F59E0B" }}>{r.passed ? (fr ? "Super révision !" : "Great revision!") : (fr ? "Allez, tu peux y arriver !" : "Come on, you can make it!")}</p>
      <p className="text-xl font-parent mt-4" data-testid="holiday-score">{r.score}/{r.total} · {r.percent}%</p>
      {r.new_avatars?.length > 0 && (
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.4 }} className="mt-6 rounded-3xl bg-orange-100 border-4 border-slate-900 p-5" data-testid="holiday-new-avatars">
          <p className="text-2xl font-black">{fr ? "Pack terminé ! Nouveaux avatars de vacances !" : "Pack finished! New holiday avatars unlocked!"}</p>
          <div className="flex justify-center gap-4 mt-4">
            {r.new_avatars.map((a) => (
              <div key={a.id} className="text-center"><img src={a.img} alt={a.name} className="w-24 h-24 rounded-full border-4 border-slate-900 object-cover mx-auto" /><p className="font-parent text-sm font-semibold mt-1">{a.name}</p></div>
            ))}
          </div>
        </motion.div>
      )}
      {!r.new_avatars?.length && left > 0 && <p className="font-parent text-sm text-orange-700 mt-3" data-testid="holiday-avatar-hint">{fr ? `Encore ${left} questions pour finir le pack et gagner les 3 avatars de vacances !` : `${left} more questions to finish the pack and win the 3 holiday avatars!`}</p>}
      <div className="flex flex-wrap justify-center gap-3 mt-6">
        <button onClick={onAgain} className="rounded-full bg-orange-500 text-white font-bold px-6 py-3 border-2 border-slate-900" data-testid="holiday-again">{fr ? "Continuer" : "Keep going"}</button>
        <button onClick={onBack} className="rounded-full border-2 border-slate-900 font-bold px-6 py-3" data-testid="holiday-done">{fr ? "Terminer" : "Done"}</button>
      </div>
    </motion.div>
  );
}
